// Displays information of a single Order in card format
import React, { useCallback, useEffect } from "react";
import { ChevronRight24Regular, Receipt24Filled } from "@fluentui/react-icons";
import { Subtitle2, mergeClasses, tokens } from "@fluentui/react-components";
import { Order, OrderStatus } from "@dining/api-client";
import { useNavigate } from "react-router-dom";
import { useStyles } from "./OrderCardStyles";
import { HelperUtils, appInsightsEvents, trackEvent, useAppStore } from "../../../common";

interface OrderCardProps {
  order?: Order;
}
export const OrderCard = ({ order }: OrderCardProps) => {
  const styles = useStyles();
  const [appInsights, context] = useAppStore((state) => [state.appInsights, state.context]);
  const helperUtils = HelperUtils.getInstance();

  const navigate = useNavigate();

  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "OrderCard",
    });
  }, [context, appInsights]);

  const getShortOrderStatusString = useCallback(() => {
    switch (order?.status) {
      case OrderStatus.SubmittedToVendor:
        return "Order Placed";
      case OrderStatus.BeingPrepared:
        return "Preparing";
      case OrderStatus.ReadyForPickup:
        return "Ready for pickup";
      case OrderStatus.CannotFulfill:
        return "There is a problem";
      default:
        return "";
    }
  }, [order]);

  const onClick = useCallback(() => {
    navigate("/cafe/" + order?.cafeId + "/order/" + order?.id);
    trackEvent(appInsights, appInsightsEvents.Usage.Actions.OrderSelected, context, {
      selectedOrderId: order?.id,
      orderCafeName: order?.cafeName,
      orderNumber: order?.orderNumber,
    });
  }, [navigate, appInsights, order]);

  const orderNumberShort = helperUtils.getLastThreeDigitsOfNum(order?.orderNumber);
  const ariaLabel = `Order ${orderNumberShort} from ${order?.cafeName}. ${getShortOrderStatusString()}. Tap to view order summary.`;

  return (
    order && (
      <div
        className={styles.container}
        onClick={onClick}
        onKeyPress={onClick}
        data-testid="orderCard"
        tabIndex={0}
        aria-label={ariaLabel}
        role="button"
      >
        {/* If order status is cannotfulfil (202), the order card in the order list is shown red */}
        <div
          className={mergeClasses(
            styles.details,
            order.status === OrderStatus.CannotFulfill ? styles.red : styles.purple,
          )}
        >
          <div className={styles.orderSection}>
            <Receipt24Filled className={styles.icon} primaryFill={tokens.colorNeutralBackground1} />
          </div>
          <div className={styles.orderSection} onClick={onClick}>
            <Subtitle2
              wrap={false}
              truncate
              className={mergeClasses(styles.text, styles.whitetext)}
            >
              Order #{orderNumberShort} &middot; {" "}{order.cafeName}
            </Subtitle2>
            <span className={mergeClasses(styles.whitetext, styles.smalltext)}>
              {getShortOrderStatusString()}
            </span>
          </div>
          <div className={mergeClasses(styles.orderSection, styles.rightAlignment)}>
            <ChevronRight24Regular
              className={styles.icon}
              primaryFill={tokens.colorNeutralBackground1}
            />
          </div>
        </div>
      </div>
    )
  );
};
